import {ChangeDetectionStrategy, Component, Inject, Injectable, signal} from '@angular/core';

import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AksjeeierbokId, AksjeeierbokModell, SlettSisteBeholdningsendringModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {AksjeeiereSlettSisteBeholdningsendringKomponent} from "../../komponenter/aksjeeiere-slett-siste-beholdningsendring/aksjeeiere-slett-siste-beholdningsendring";
import {Temporal} from "temporal-polyfill";
import {AksjeeierDatatjeneste} from "felles-ng2/tjenester/datalager/AksjeeierDatatjeneste";
import {Feilhåndteringsstrategi} from "felles-ng2/tjenester/datalager/datatjeneste";
import {HttpErrorResponse} from "@angular/common/http";
import {HttpResponskoder} from "felles-ng2/infrastruktur/http/httpResponskoder";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {fadeInnAnimasjon} from "web-ng2/delt/animasjoner";
import {scrollTilBunn} from "web-ng2/delt/dom/scrollIntoView";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TranslateModule} from "@ngx-translate/core";
import {lesPlainDateSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {LasterDataKomponent} from "web-ng2/delt/komponenter/laster-data/laster-data";
import PlainDate = Temporal.PlainDate;

const debug = initDebugger('AksjeeiereSlettSisteBeholdningsendringDialogKomponent');

/**
 * Modal for sletting av seneste beholdningsendring.
 * Merk at denne forutsetter at aksjeeierbok inneholder beholdninger.
 */
@Component({
    selector: 'aksjeeiere-slett-siste-beholdningsendring-dialog',
    templateUrl: './aksjeeiere-slett-siste-beholdningsendring-dialog.html',
    styleUrls: ['./aksjeeiere-slett-siste-beholdningsendring-dialog.scss'],
    imports: [
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatIconModule,
        MatToolbarModule,
        TranslateModule,
        VisSpinnerDirektiv,
        ReactiveFormsModule,
        AksjeeiereSlettSisteBeholdningsendringKomponent,
        InlineMeldingKomponent,
        LasterDataKomponent
    ],
    animations: [fadeInnAnimasjon()],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeeiereSlettSisteBeholdningsendringDialogKomponent {

    static vis(id: AksjeeierbokId, dialog: MatDialog): MatDialogRef<AksjeeiereSlettSisteBeholdningsendringDialogKomponent, void> {
        return dialog.open(AksjeeiereSlettSisteBeholdningsendringDialogKomponent, {data: {id: id} as DialogParametre, disableClose: true});
    }

    private id!: AksjeeierbokId

    slettInfoModell = signal<SlettSisteBeholdningsendringModell | null>(null);
    lagrer = signal(false);
    serverResponderteMedValideringsfeil = signal(false);

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                public dialogRef: MatDialogRef<AksjeeiereSlettSisteBeholdningsendringDialogKomponent>,
                private avhengigheter: AksjeeiereSlettSisteBeholdningsendringDialogKomponentAvhengigheter,
                private meldingTilBruker: MeldingTilBruker) {
        debug("konstr", parametre)
        this.lastData(parametre.id);
    }

    avbryt() {
        this.dialogRef.close();
    }

    utfoerSlett() {
        this.lagrer.set(true);
        this.avhengigheter.utførSlett(this.id, lesPlainDateSerialisert(this.slettInfoModell()!.sisteInnfoeringsdato!))
          .then(() => this.slettingOk())
          .catch((feil: HttpErrorResponse) => this.håndterFeil(feil))
          .finally(() => {
              this.lagrer.set(false);
          });
    }

    //private hjelpemetoder

    private slettingOk() {
        this.dialogRef.close();
        this.meldingTilBruker.infomelding(this.avhengigheter.oversett(i18n('Siste beholdningsoppdatering har blitt slettet')));
    }

    private async lastData(id: AksjeeierbokId) {
        debug('lastData', id)
        this.id = id;
        this.slettInfoModell.set(await this.avhengigheter.hentInfo(id));
    }

    private håndterFeil(feil: HttpErrorResponse) {
        if (feil.status == HttpResponskoder.ValideringsfeilException) {
            this.serverResponderteMedValideringsfeil.set(true);
            this.lastData(this.id);
            //scroll til valideringsfeil-melding - med forsinkelse slik at melding har blitt rendret
            scrollTilBunn('#assbd-valideringsfeil', 100);
        } else {
            this.meldingTilBruker.feilmelding(this.avhengigheter.oversett(i18n('Kunne ikke angre siste beholdningsoppdatering')));
        }
    }
}

export interface DialogParametre {
    id: AksjeeierbokId;
}

@Injectable()
export class AksjeeiereSlettSisteBeholdningsendringDialogKomponentAvhengigheter {

    constructor(private aksjeeierDatatjeneste: AksjeeierDatatjeneste, private oversettelser: Oversettelser) {
    }

    oversett(nøkkel: string) {
        return this.oversettelser.oversett(nøkkel);
    }

    hentInfo(id: AksjeeierbokId): Promise<SlettSisteBeholdningsendringModell> {
        return this.aksjeeierDatatjeneste.hentSlettSisteBeholdningsendringInfo(id);
    }

    /** kaller {@link AksjeeierDatatjeneste#slettSisteBeholdningsendring}.
     * Merkt at feilet kall propageres via Promise.error, kaller bør håndtere httpStatus 417 (EXPECTATION_FAILED)
     * som indikerer at Aksjeeierbok.sisteInnføringsdato ikke er lik angitt innføringsdatoSomSlettes */
    utførSlett(id: AksjeeierbokId, innføringsdatoSomSlettes: PlainDate): Promise<AksjeeierbokModell> {
        return this.aksjeeierDatatjeneste.slettSisteBeholdningsendring(id, innføringsdatoSomSlettes, Feilhåndteringsstrategi.propagerSomError);
    }
}
